/**
 * WeaponLabOverlayRenderer — Weapon Lab pause overlay (training mode).
 * Keyboard/gamepad only, no touch handlers.
 *
 * Extracted from MenuRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, Z } from '../../constants';
import { formatWind } from '../../game/wind';
import { lookupTank } from '../../tanks';
import type { GameState } from '../../types';

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

export class WeaponLabOverlayRenderer {
    private labContainer: Container;
    private labOverlay: Graphics;
    private labTitle: Text;
    private labRowLabels: Text[] = [];
    private labRowValues: Text[] = [];
    private labCursorGfx: Graphics;

    constructor(uiContainer: Container) {
        this.labContainer = new Container();
        this.labContainer.visible = false;
        this.labContainer.zIndex = Z.MENU_UI + 11;
        uiContainer.addChild(this.labContainer);

        const cx = WORLD.CANVAS_WIDTH / 2;

        this.labOverlay = new Graphics();
        this.labOverlay.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        this.labOverlay.fill({ color: 0x000000, alpha: 0.85 });
        this.labOverlay.eventMode = 'static';
        this.labContainer.addChild(this.labOverlay);

        this.labTitle = new Text({
            text: 'WEAPON LAB',
            style: monoStyle(36, 0x44ff88, true),
        });
        this.labTitle.anchor.set(0.5);
        this.labTitle.position.set(cx, 200);
        this.labContainer.addChild(this.labTitle);

        this.labCursorGfx = new Graphics();
        this.labCursorGfx.zIndex = 1;
        this.labContainer.addChild(this.labCursorGfx);

        const LAB_LABELS = ['TANK', 'SHOT TIER', 'WIND', 'DUMMY DAMAGE', 'RESET DUMMY', 'RESET TERRAIN', 'RESUME', 'MAIN MENU'];
        for (let i = 0; i < LAB_LABELS.length; i++) {
            const rowY = 280 + i * 48;
            const label = new Text({
                text: LAB_LABELS[i],
                style: monoStyle(20, 0x888888, true),
            });
            label.anchor.set(1, 0.5);
            label.position.set(cx - 30, rowY);
            this.labContainer.addChild(label);
            this.labRowLabels.push(label);

            const value = new Text({
                text: '',
                style: monoStyle(20, 0xffffff),
            });
            value.anchor.set(0, 0.5);
            value.position.set(cx + 30, rowY);
            this.labContainer.addChild(value);
            this.labRowValues.push(value);
        }
    }

    setVisible(v: boolean): void {
        this.labContainer.visible = v;
    }

    update(state: GameState, elapsed: number): void {
        const cx = WORLD.CANVAS_WIDTH / 2;
        const ms = state.modeState;
        const player = state.players[0];
        const cursor = ms.labCursor;

        const tankName = lookupTank(player.tankId).name.toUpperCase();
        this.labRowValues[0].text = `\u25C4 ${tankName} \u25BA`;

        this.labRowValues[1].text = `[${player.selectedShotTier === 1 ? '\u25CF' : '\u25CB'}]  [${player.selectedShotTier === 2 ? '\u25CF' : '\u25CB'}]  [${player.selectedShotTier === 3 ? '\u25CF' : '\u25CB'}]`;

        if (ms.labWindOverride === null) {
            const windInfo = formatWind(state.wind);
            this.labRowValues[2].text = `${windInfo.display}  [GAME]`;
        } else {
            const windInfo = formatWind(ms.labWindOverride);
            this.labRowValues[2].text = `\u25C4 ${windInfo.display} \u25BA`;
        }

        this.labRowValues[3].text = `\u25C4 ${ms.labDummyDamage}% \u25BA`;

        this.labRowValues[4].text = '';
        this.labRowValues[5].text = '';
        this.labRowValues[6].text = '';
        this.labRowValues[7].text = '';

        for (let i = 0; i < this.labRowLabels.length; i++) {
            const isSelected = i === cursor;
            this.labRowLabels[i].style.fill = isSelected ? 0xffffff : 0x666666;
            this.labRowValues[i].style.fill = isSelected ? 0x44ff88 : 0x888888;

            if (isSelected) {
                const pulse = 0.7 + 0.3 * Math.sin(elapsed * 4);
                this.labRowLabels[i].alpha = pulse;
                this.labRowValues[i].alpha = pulse;
            } else {
                this.labRowLabels[i].alpha = 1;
                this.labRowValues[i].alpha = 1;
            }
        }

        this.labCursorGfx.clear();
        const rowY = 280 + cursor * 48;
        const barW = 500;
        const barH = 36;
        const r = 6;
        this.labCursorGfx.roundRect(cx - barW / 2, rowY - barH / 2, barW, barH, r);
        this.labCursorGfx.fill({ color: 0x44ff88, alpha: 0.08 });
        this.labCursorGfx.roundRect(cx - barW / 2, rowY - barH / 2, barW, barH, r);
        this.labCursorGfx.stroke({ color: 0x44ff88, width: 1, alpha: 0.2 });
    }

    destroy(): void {
        this.labContainer.destroy({ children: true });
    }
}
